<template>
  <div class="weightloss-referral-step mt-2">
    <b-row>
      <b-col cols="12" class="mb-3">
        <h5 class="mb-0 text-primary">
          {{ isConsume ? 'Weight loss — pending dose' : 'Weight loss treatment' }}
        </h5>
        <small class="text-muted">
          <template v-if="isConsume">
            Apply one dose from the patient's active balance. No charge is added for weightloss.
          </template>
          <template v-else>
            Optional. Select the medication to apply today and schedule the follow-up appointment.
          </template>
        </small>
      </b-col>
    </b-row>

    <b-alert
      v-if="isConsume && balancesError"
      show
      variant="warning"
      class="mb-3 py-2">
      {{ balancesError }}
    </b-alert>

    <b-alert
      v-if="isConsume && !balancesLoading && !hasBalances && !hasHydratedBalanceId"
      show
      variant="secondary"
      class="mb-3 py-2">
      This patient has no pending weightloss doses. The weightloss block is unavailable for this visit.
    </b-alert>

    <b-row>
      <b-col cols="12" class="mb-3">
        <b-card no-body class="border shadow-sm rounded">
          <b-card-body class="py-3">
            <div class="d-flex flex-wrap align-items-center justify-content-between">
              <div class="mb-2 mb-md-0">
                <span class="font-weight-bold text-dark">
                  {{ isConsume ? 'Apply pending dose' : 'Sell and apply weight loss treatment' }}
                </span>
                <small class="d-block text-muted">
                  <template v-if="isConsume">
                    Enable to consume one dose from a pending balance ($0 weightloss charge).
                  </template>
                  <template v-else>
                    Leave disabled for a regular office visit without a weightloss charge.
                  </template>
                </small>
              </div>
              <b-form-checkbox
                v-model="enabledLocal"
                switch
                size="lg"
                class="mb-0"
                :disabled="toggleDisabled"
                @change="onToggle">
                <span class="ml-2">
                  {{
                    enabledLocal
                      ? (isConsume ? 'Include dose application' : 'Include weightloss')
                      : 'Do not include'
                  }}
                </span>
              </b-form-checkbox>
            </div>
            <small v-if="lockEnabled && enabledLocal" class="d-block text-muted mt-2">
              This visit already has weightloss saved; the block cannot be removed from this flow.
            </small>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- CONSUME MODE -->
    <template v-if="isConsume && enabledLocal">
      <b-row>
        <b-col cols="12" md="8">
          <b-form-group label="Pending dose *" label-for="wl-balance">
            <div v-if="balancesLoading" class="text-muted small">Loading pending balances…</div>
            <treeselect
              v-else
              id="wl-balance"
              :value="formLocal.weightloss_dose_balance_id"
              :options="balanceOptions"
              :clearable="false"
              :disabled="balanceOptions.length === 0"
              placeholder="Select pending dose"
              @input="onBalanceInput" />
            <small v-if="selectedBalance" class="text-muted d-block mt-1">
              Paid {{ selectedBalance.qty_paid }} · remaining {{ selectedBalance.qty_remaining }}
              · ${{ selectedBalance.total_price_paid }}
            </small>
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-card no-body class="border rounded mb-3">
            <b-card-body class="py-3">
              <h6 class="font-weight-bold mb-2">Pricing</h6>
              <div class="d-flex justify-content-between">
                <strong>Weightloss total</strong>
                <strong class="text-primary">$0.00 USD</strong>
              </div>
              <small class="text-muted">
                Dose consumption does not add a charge. Other clinical items still apply normally.
              </small>
            </b-card-body>
          </b-card>
        </b-col>

        <b-col cols="12">
          <b-form-group label="Note" label-for="wl-note-consume">
            <b-form-textarea
              id="wl-note-consume"
              v-model="formLocal.note"
              rows="3"
              placeholder="Optional"
              @input="emitForm" />
          </b-form-group>
        </b-col>
      </b-row>
    </template>

    <!-- SELL MODE -->
    <template v-else-if="!isConsume && enabledLocal">
      <b-row>
        <b-col cols="12" md="6">
          <b-form-group label="Treatment *" label-for="wl-treatment">
            <treeselect
              id="wl-treatment"
              :value="formLocal.weightloss_treatment_id"
              :options="treatmentOptions"
              :clearable="false"
              :disabled="loadingTreatments"
              placeholder="Select treatment"
              @input="onTreatmentInput" />
          </b-form-group>
        </b-col>

        <b-col cols="12" md="3">
          <b-form-group label="Dose *" label-for="wl-dose">
            <treeselect
              id="wl-dose"
              :value="formLocal.weightloss_dose_id"
              :options="doseOptions"
              :clearable="false"
              :disabled="!formLocal.weightloss_treatment_id || loadingDoses"
              placeholder="Select dose"
              @input="onDoseInput" />
          </b-form-group>
        </b-col>

        <b-col cols="12" md="3">
          <b-form-group label="Qty doses *" label-for="wl-qty-doses">
            <b-form-select
              id="wl-qty-doses"
              v-model="formLocal.qty_doses"
              :options="qtyDoseOptions"
              @change="emitAndQuote" />
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6">
          <b-form-group label="Follow-up appointment *" label-for="wl-visit-date">
            <flat-pickr
              id="wl-visit-date"
              v-model="formLocal.visit_date"
              :config="dateConfig"
              class="form-control"
              placeholder="MM-DD-YYYY HH:mm:ss"
              @on-change="onFollowupDateChange" />
            <small class="text-muted">Future appointment. Format: MM-DD-YYYY HH:mm:ss.</small>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6">
          <b-form-group label="Goal weight (Lb)" label-for="wl-goal-weight">
            <b-form-input
              id="wl-goal-weight"
              v-model="formLocal.goal_weight"
              type="number"
              step="0.01"
              min="0"
              placeholder="Optional"
              @input="emitForm" />
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group label="Fat burners / add-ons">
            <div
              v-for="(row, index) in formLocal.addons"
              :key="row._key"
              class="d-flex flex-wrap align-items-end mb-2">
              <div class="flex-grow-1 mr-2 mb-2">
                <treeselect
                  :value="row.weightloss_addon_id"
                  :options="addonOptions"
                  placeholder="Select add-on"
                  @input="value => onAddonInput(index, value)" />
              </div>
              <b-form-group label="Qty" class="mr-2 mb-2" style="min-width: 6rem">
                <b-form-input
                  v-model="row.qty"
                  type="number"
                  min="1"
                  step="1"
                  @input="emitAndQuote" />
              </b-form-group>
              <b-button
                variant="outline-danger"
                size="sm"
                class="mb-2"
                @click="removeAddon(index)">
                <i class="fas fa-times"></i>
              </b-button>
            </div>
            <b-button variant="outline-primary" size="sm" @click="addAddon">
              <i class="fas fa-plus mr-1"></i> Add fat burner
            </b-button>
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-card no-body class="border rounded mb-3">
            <b-card-body class="py-3">
              <h6 class="font-weight-bold mb-2">Pricing preview</h6>
              <div v-if="quoteLoading" class="text-muted small">Calculating price…</div>
              <b-alert v-else-if="quoteError" show variant="warning" class="mb-0 py-2">
                {{ quoteError }}
              </b-alert>
              <div v-else-if="pricing">
                <div v-if="pricing.main_line" class="d-flex justify-content-between mb-1">
                  <span>
                    {{ treatmentLabel }}
                    <span v-if="pricing.main_line.dose_label">— {{ pricing.main_line.dose_label }}</span>
                    <small class="text-muted ml-1">
                      {{ pricing.main_line.qty_doses }} dose(s)
                      <template
                        v-if="pricing.main_line.pricing_source === 'unit' && pricing.main_line.unit_price">
                        × ${{ pricing.main_line.unit_price }}
                      </template>
                      <template v-else-if="pricing.main_line.pricing_source">
                        · {{ pricing.main_line.pricing_source }}
                      </template>
                    </small>
                  </span>
                  <strong>${{ pricing.main_line.line_total }}</strong>
                </div>

                <template v-if="pricing.addons && pricing.addons.length">
                  <div class="font-weight-bold small mt-2 mb-1">
                    {{ pricing.addons_group_label || 'Fat burners' }}
                  </div>
                  <div
                    v-for="addon in pricing.addons"
                    :key="addon.weightloss_addon_id"
                    class="d-flex justify-content-between small pl-2 mb-1">
                    <span>
                      {{ addon.name }} · {{ addon.qty }} application(s)
                      <span v-if="addon.unit_price" class="text-muted">× ${{ addon.unit_price }}</span>
                    </span>
                    <strong>${{ addon.line_total }}</strong>
                  </div>
                </template>

                <hr class="my-2" />
                <div class="d-flex justify-content-between">
                  <strong>Weightloss total</strong>
                  <strong class="text-primary">${{ pricing.total }} {{ pricing.currency || 'USD' }}</strong>
                </div>
              </div>
              <small v-else class="text-muted">
                {{ quoteBlockedHint }}
              </small>
            </b-card-body>
          </b-card>
        </b-col>

        <b-col cols="12">
          <b-form-group label="Note" label-for="wl-note">
            <b-form-textarea
              id="wl-note"
              v-model="formLocal.note"
              rows="3"
              placeholder="Optional"
              @input="emitForm" />
          </b-form-group>
        </b-col>
      </b-row>
    </template>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BAlert,
  BCard,
  BCardBody,
  BButton,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
} from 'bootstrap-vue'
import Treeselect from '@riophae/vue-treeselect'
import flatPickr from 'vue-flatpickr-component'
import wlTreatmentsApi from '@/core/services/api/weightloss-inventory/treatments'
import wlDosesApi from '@/core/services/api/weightloss-inventory/doses'
import wlAddonsApi from '@/core/services/api/weightloss-inventory/addons'
import wlPricingApi from '@/core/services/api/weightloss-inventory/pricing'
import {
  toProjectDate,
  toProjectDateTime,
  todayProjectDate,
} from '@/core/services/api/weightloss-inventory/dateFormat'

let addonKey = 0

function normalizeAddons(addons = []) {
  return (addons || []).map(addon => ({
    _key: addon._key || `oma-wl-addon-${++addonKey}`,
    weightloss_addon_id: addon.weightloss_addon_id || null,
    qty: addon.qty || 1,
  }))
}

function formFingerprint(form = {}, mode = 'sell') {
  return JSON.stringify({
    mode,
    weightloss_dose_balance_id: form.weightloss_dose_balance_id ?? null,
    weightloss_treatment_id: form.weightloss_treatment_id ?? null,
    weightloss_dose_id: form.weightloss_dose_id ?? null,
    qty_doses: Number(form.qty_doses) || 1,
    addons: (form.addons || []).map(a => ({
      weightloss_addon_id: a.weightloss_addon_id || null,
      qty: Number(a.qty) || 1,
    })),
    goal_weight: form.goal_weight ?? '',
    visit_date: form.visit_date ?? '',
    note: form.note ?? '',
  })
}

export default {
  name: 'WeightlossReferralStep',
  components: {
    BRow,
    BCol,
    BAlert,
    BCard,
    BCardBody,
    BButton,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    Treeselect,
    flatPickr,
  },
  props: {
    enabled: {
      type: Boolean,
      default: false,
    },
    form: {
      type: Object,
      required: true,
    },
    mode: {
      type: String,
      default: 'sell',
      validator: value => ['sell', 'consume'].includes(value),
    },
    balances: {
      type: Array,
      default: () => [],
    },
    balancesLoading: {
      type: Boolean,
      default: false,
    },
    balancesError: {
      type: String,
      default: '',
    },
    lockEnabled: {
      type: Boolean,
      default: false,
    },
    facilityId: {
      type: [Number, String],
      default: null,
    },
    quoteVisitDate: {
      type: [String, Date],
      default: null,
    },
    weightlossPatientId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      enabledLocal: false,
      syncingFromParent: false,
      lastParentFingerprint: '',
      formLocal: {
        weightloss_treatment_id: null,
        weightloss_dose_id: null,
        weightloss_dose_balance_id: null,
        qty_doses: 1,
        addons: [],
        goal_weight: '',
        visit_date: '',
        note: '',
      },
      treatmentOptions: [],
      doseOptions: [],
      addonOptions: [],
      loadingTreatments: false,
      loadingDoses: false,
      pricing: null,
      quoteLoading: false,
      quoteError: '',
      quoteTimer: null,
      quoteSequence: 0,
      qtyDoseOptions: [1, 2, 3, 4].map(value => ({
        value,
        text: String(value),
      })),
      dateConfig: {
        dateFormat: 'm-d-Y H:i:S',
        allowInput: true,
        enableTime: true,
        enableSeconds: true,
        time_24hr: true,
      },
    }
  },
  computed: {
    isConsume() {
      return this.mode === 'consume'
    },
    hasBalances() {
      return (this.balances || []).length > 0
    },
    hasHydratedBalanceId() {
      return (
        this.formLocal.weightloss_dose_balance_id != null &&
        this.formLocal.weightloss_dose_balance_id !== ''
      )
    },
    toggleDisabled() {
      if (this.lockEnabled && this.enabledLocal) return true
      if (this.isConsume && !this.hasBalances && !this.hasHydratedBalanceId && !this.lockEnabled) {
        return true
      }
      return false
    },
    balanceOptions() {
      const options = (this.balances || []).map(balance => ({
        id: balance.weightloss_dose_balance_id,
        label: [
          balance.treatment_name || 'Treatment',
          balance.dose_label || 'Dose',
          `quedan ${balance.qty_remaining}`,
        ].join(' — '),
      }))
      // Reenvío: saldo agotado ya no viene en la lista; conservar opción hidratada.
      const selectedId = this.formLocal.weightloss_dose_balance_id
      if (
        selectedId != null &&
        selectedId !== '' &&
        !options.some(o => Number(o.id) === Number(selectedId))
      ) {
        options.unshift({
          id: Number(selectedId),
          label: `Balance #${selectedId} (already applied on this visit)`,
        })
      }
      return options
    },
    selectedBalance() {
      const id = this.formLocal.weightloss_dose_balance_id
      if (id == null || id === '') return null
      return (this.balances || []).find(
        b => Number(b.weightloss_dose_balance_id) === Number(id)
      ) || null
    },
    treatmentLabel() {
      if (this.isConsume) {
        if (this.selectedBalance) {
          return `${this.selectedBalance.treatment_name || 'Weight loss'} — ${
            this.selectedBalance.dose_label || ''
          }`.trim()
        }
        return 'Weight loss (pending dose)'
      }
      const treatment = this.treatmentOptions.find(
        item => Number(item.id) === Number(this.formLocal.weightloss_treatment_id)
      )
      return treatment ? treatment.label : 'Weight loss'
    },
    /** Fecha clínica para el quote; si falta la de la cita, usa hoy. */
    resolvedQuoteVisitDate() {
      return toProjectDate(this.quoteVisitDate) || todayProjectDate()
    },
    quoteBlockedHint() {
      if (!this.enabledLocal) {
        return 'Enable weightloss to calculate the backend price.'
      }
      if (!this.formLocal.weightloss_treatment_id) {
        return 'Select a treatment to calculate the backend price.'
      }
      if (!this.formLocal.weightloss_dose_id) {
        return 'Select a dose to calculate the backend price.'
      }
      if (
        !(Number(this.formLocal.qty_doses) >= 1 && Number(this.formLocal.qty_doses) <= 4)
      ) {
        return 'Qty doses must be between 1 and 4.'
      }
      return 'Waiting to calculate price…'
    },
  },
  watch: {
    enabled: {
      immediate: true,
      handler(value) {
        this.enabledLocal = !!value
        if (value) this.scheduleQuote()
        else this.emitConsumePricingIfNeeded()
      },
    },
    mode: {
      immediate: true,
      handler() {
        this.scheduleQuote()
        this.emitConsumePricingIfNeeded()
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler(value) {
        if (!value || this.syncingFromParent) return
        const fingerprint = formFingerprint(value, this.mode)
        if (fingerprint === this.lastParentFingerprint) return

        const previousTreatmentId = this.formLocal.weightloss_treatment_id
        const treatmentChanged =
          !this.isConsume &&
          value.weightloss_treatment_id &&
          Number(previousTreatmentId) !== Number(value.weightloss_treatment_id)

        this.syncingFromParent = true
        this.lastParentFingerprint = fingerprint
        this.formLocal = {
          weightloss_treatment_id: value.weightloss_treatment_id ?? null,
          weightloss_dose_id: value.weightloss_dose_id ?? null,
          weightloss_dose_balance_id: value.weightloss_dose_balance_id ?? null,
          qty_doses: Number(value.qty_doses) || 1,
          addons: normalizeAddons(value.addons),
          goal_weight: value.goal_weight ?? '',
          visit_date: value.visit_date ?? '',
          note: value.note ?? '',
        }
        this.$nextTick(async () => {
          this.syncingFromParent = false
          if (treatmentChanged) {
            await this.loadDoses(false)
          }
          this.scheduleQuote()
          this.emitConsumePricingIfNeeded()
        })
      },
    },
    facilityId() {
      if (!this.isConsume) {
        this.loadAddons()
        this.scheduleQuote()
      }
    },
    quoteVisitDate() {
      if (!this.isConsume) this.scheduleQuote()
    },
    weightlossPatientId() {
      if (!this.isConsume) this.scheduleQuote()
    },
  },
  async created() {
    if (!this.isConsume) {
      await Promise.all([this.loadTreatments(), this.loadAddons()])
      if (this.formLocal.weightloss_treatment_id) {
        await this.loadDoses(false)
      }
      this.scheduleQuote()
    } else {
      this.emitConsumePricingIfNeeded()
    }
  },
  beforeDestroy() {
    if (this.quoteTimer) clearTimeout(this.quoteTimer)
  },
  methods: {
    emitConsumePricingIfNeeded() {
      if (!this.isConsume) return
      this.pricing = { total: 0 }
      this.quoteError = ''
      this.quoteLoading = false
      this.$emit('pricing', {
        pricing: this.pricing,
        total: 0,
        error: '',
        loading: false,
        treatmentLabel: this.treatmentLabel,
      })
    },
    async loadTreatments() {
      this.loadingTreatments = true
      try {
        const treatments = await wlTreatmentsApi.list()
        this.treatmentOptions = (treatments || []).map(treatment => ({
          id: treatment.id,
          label: treatment.name,
        }))
      } catch (error) {
        this.treatmentOptions = []
      } finally {
        this.loadingTreatments = false
      }
    },
    async loadDoses(resetSelection = true) {
      if (resetSelection) this.formLocal.weightloss_dose_id = null
      if (!this.formLocal.weightloss_treatment_id) {
        this.doseOptions = []
        return
      }

      this.loadingDoses = true
      try {
        const doses = await wlDosesApi.list({
          weightloss_treatment_id: this.formLocal.weightloss_treatment_id,
          active: true,
        })
        this.doseOptions = (doses || []).map(dose => ({
          id: dose.id,
          label: dose.dose_label,
        }))
      } catch (error) {
        this.doseOptions = []
      } finally {
        this.loadingDoses = false
      }
    },
    async loadAddons() {
      const params = { active: true }
      if (this.facilityId != null && this.facilityId !== '') {
        params.facility_id = Number(this.facilityId)
      }
      try {
        const addons = await wlAddonsApi.list(params)
        this.addonOptions = (addons || []).map(addon => {
          const hasPrice = addon.price != null && addon.price !== ''
          return {
            id: addon.id,
            label: hasPrice
              ? `${addon.name} ($${Number(addon.price).toFixed(2)})`
              : `${addon.name} (no price — not chargeable)`,
            isDisabled: !hasPrice,
          }
        })
      } catch (error) {
        this.addonOptions = []
      }
    },
    /** Asigna el valor del evento ANTES del quote (evita race con v-model). */
    async onTreatmentInput(value) {
      this.formLocal.weightloss_treatment_id = value
      await this.loadDoses(true)
      this.emitAndQuote()
    },
    onDoseInput(value) {
      this.formLocal.weightloss_dose_id = value
      this.emitAndQuote()
    },
    onBalanceInput(value) {
      this.formLocal.weightloss_dose_balance_id = value
      this.emitForm()
      this.emitConsumePricingIfNeeded()
    },
    onAddonInput(index, value) {
      if (!this.formLocal.addons[index]) return
      this.formLocal.addons[index].weightloss_addon_id = value
      this.emitAndQuote()
    },
    onFollowupDateChange(selectedDates, dateStr) {
      const raw = dateStr || (selectedDates && selectedDates[0]) || this.formLocal.visit_date
      this.formLocal.visit_date = toProjectDateTime(raw) || ''
      this.emitForm()
    },
    addAddon() {
      this.formLocal.addons.push({
        _key: `oma-wl-addon-${++addonKey}`,
        weightloss_addon_id: null,
        qty: 1,
      })
      this.emitForm()
    },
    removeAddon(index) {
      this.formLocal.addons.splice(index, 1)
      this.emitAndQuote()
    },
    buildAddonsPayload() {
      return this.formLocal.addons
        .filter(addon => addon.weightloss_addon_id)
        .map(addon => ({
          weightloss_addon_id: Number(addon.weightloss_addon_id),
          qty: Number(addon.qty) || 1,
        }))
    },
    canQuote() {
      if (this.isConsume) return false
      return (
        this.enabledLocal &&
        this.formLocal.weightloss_treatment_id != null &&
        this.formLocal.weightloss_treatment_id !== '' &&
        this.formLocal.weightloss_dose_id != null &&
        this.formLocal.weightloss_dose_id !== '' &&
        Number(this.formLocal.qty_doses) >= 1 &&
        Number(this.formLocal.qty_doses) <= 4 &&
        !!this.resolvedQuoteVisitDate
      )
    },
    buildQuotePayload() {
      const payload = {
        weightloss_treatment_id: Number(this.formLocal.weightloss_treatment_id),
        weightloss_dose_id: Number(this.formLocal.weightloss_dose_id),
        qty_doses: Number(this.formLocal.qty_doses),
        visit_date: this.resolvedQuoteVisitDate,
      }
      if (this.facilityId != null && this.facilityId !== '') {
        payload.facility_id = Number(this.facilityId)
      } else {
        payload.facility_id = null
      }
      if (this.weightlossPatientId) {
        payload.weightloss_patient_id = Number(this.weightlossPatientId)
      }
      const addons = this.buildAddonsPayload()
      if (addons.length) payload.addons = addons
      return payload
    },
    scheduleQuote() {
      if (this.isConsume) {
        if (this.quoteTimer) clearTimeout(this.quoteTimer)
        this.emitConsumePricingIfNeeded()
        return
      }
      if (this.quoteTimer) clearTimeout(this.quoteTimer)
      // No limpiar pricing aquí: evita parpadeo / placeholder mientras llega el nuevo quote
      this.quoteTimer = setTimeout(() => this.fetchQuote(), 350)
    },
    async fetchQuote() {
      if (this.isConsume) {
        this.emitConsumePricingIfNeeded()
        return
      }

      if (!this.enabledLocal) {
        this.pricing = null
        this.quoteError = ''
        this.quoteLoading = false
        this.emitPricing()
        return
      }

      if (!this.canQuote()) {
        this.pricing = null
        this.quoteError = ''
        this.quoteLoading = false
        this.emitPricing()
        return
      }

      const sequence = ++this.quoteSequence
      this.quoteLoading = true
      this.quoteError = ''
      this.emitPricing()
      try {
        const pricing = await wlPricingApi.quote(this.buildQuotePayload())
        if (sequence !== this.quoteSequence) return
        if (!pricing || (pricing.total == null && !pricing.main_line)) {
          this.pricing = null
          this.quoteError =
            'Backend returned an empty price. Check GLP-1 Prices for this treatment and dose.'
        } else {
          this.pricing = pricing
          this.quoteError = ''
        }
      } catch (error) {
        if (sequence !== this.quoteSequence) return
        this.pricing = null
        const msg = error.message || 'Could not calculate weightloss price'
        if (/no (unit )?price/i.test(msg) || /not found/i.test(msg) || /no price/i.test(msg)) {
          this.quoteError =
            `${msg} Add a unit price in Weightloss → GLP-1 Prices for this treatment and dose ` +
            `(effective on or before ${this.resolvedQuoteVisitDate}).`
        } else {
          this.quoteError = msg
        }
      } finally {
        if (sequence === this.quoteSequence) {
          this.quoteLoading = false
          this.emitPricing()
        }
      }
    },
    emitPricing() {
      if (this.isConsume) {
        this.emitConsumePricingIfNeeded()
        return
      }
      const total = this.pricing ? Number(this.pricing.total) : null
      this.$emit('pricing', {
        pricing: this.pricing,
        total: Number.isFinite(total) ? total : null,
        error: this.quoteError,
        loading: this.quoteLoading,
        treatmentLabel: this.treatmentLabel,
      })
    },
    emitForm() {
      if (this.syncingFromParent) return
      const next = this.isConsume
        ? {
            mode: 'consume',
            weightloss_dose_balance_id: this.formLocal.weightloss_dose_balance_id,
            weightloss_treatment_id: null,
            weightloss_dose_id: null,
            qty_doses: 1,
            addons: [],
            goal_weight: '',
            visit_date: '',
            note: this.formLocal.note,
          }
        : {
            mode: 'sell',
            weightloss_dose_balance_id: null,
            weightloss_treatment_id: this.formLocal.weightloss_treatment_id,
            weightloss_dose_id: this.formLocal.weightloss_dose_id,
            qty_doses: Number(this.formLocal.qty_doses) || 1,
            addons: this.buildAddonsPayload(),
            goal_weight: this.formLocal.goal_weight,
            visit_date: this.formLocal.visit_date,
            note: this.formLocal.note,
          }
      this.lastParentFingerprint = formFingerprint(next, this.mode)
      this.$emit('change', next)
    },
    emitAndQuote() {
      this.emitForm()
      this.scheduleQuote()
    },
    onToggle() {
      if (this.lockEnabled && !this.enabledLocal) {
        this.enabledLocal = true
        return
      }
      this.$emit('enabled', this.enabledLocal)
      if (!this.enabledLocal) {
        this.pricing = null
        this.quoteError = ''
        this.quoteLoading = false
        this.emitPricing()
      } else if (this.isConsume) {
        this.emitConsumePricingIfNeeded()
      } else {
        this.scheduleQuote()
      }
      this.emitForm()
    },
  },
}
</script>
